/**
 * A queued settings save, completed with what its device didn't know (audit
 * 2026-10-21). The server replaces the whole settings record on every save,
 * so a field the device never had — Correctables (corr1), on an iPad last
 * opened before they existed — would go up empty, or missing, and wipe the
 * team's. Such fields go back as the server has them, read just before the
 * save is sent:
 * - the ones the save lists in `unknownFields` (a field this device only
 *   filled in, never loaded nor edited);
 * - Correctables in a save that has Production but not them — one queued by
 *   a build from before Correctables.
 * A field the server doesn't have either stays as the device sent it.
 */

type FieldLike = { name?: unknown; [key: string]: unknown };

export interface QueuedSettings {
  fields?: FieldLike[];
  /** Fields this device doesn't actually know: take the server's. */
  unknownFields?: string[];
  [key: string]: unknown;
}

/** The fields a queued save leaves to the server's copy. */
export function unknownSettingsFields(payload: QueuedSettings): string[] {
  const names = new Set((payload.fields ?? []).map((f) => f?.name));
  const unknown = new Set(payload.unknownFields ?? []);
  if (names.has("prod1") && !names.has("corr1")) unknown.add("corr1");
  return [...unknown];
}

/**
 * The save to send: its unknown fields as the server has them (`readServer`
 * gives the server's fields, as the save's own app), and no bookkeeping.
 * Throws when the server can't be read — the save then stays queued.
 */
export async function completeSettingsPayload(
  payload: QueuedSettings,
  readServer: () => Promise<FieldLike[] | null | undefined>,
): Promise<QueuedSettings> {
  const { unknownFields: _bookkeeping, ...rest } = payload;
  void _bookkeeping;
  const unknown = unknownSettingsFields(payload);
  if (unknown.length === 0 || !Array.isArray(rest.fields)) return rest;
  const server = (await readServer()) ?? [];
  const fields = [...rest.fields];
  for (const name of unknown) {
    const theirs = server.find((f) => f?.name === name);
    if (!theirs) continue;
    const at = fields.findIndex((f) => f?.name === name);
    if (at >= 0) fields[at] = theirs;
    else fields.push(theirs);
  }
  return { ...rest, fields };
}
